import { adminApi } from '../includes/contentManagementApi';

export default {
  /**
   * Run a dry-run migration: preview what would be migrated without persisting.
   * @param {string} tenantSlug - Optional tenant slug for single-tenant migration
   * @returns {Promise} Migration preview data (migrated = would migrate, skipped = would skip)
   */
  runMigrationDryRun(tenantSlug = null) {
    const params = { dryRun: 'true' };
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-config', {}, { params });
  },

  /**
   * Run the actual migration (persists to DB).
   * @param {string} tenantSlug - Optional tenant slug for single-tenant migration
   * @returns {Promise} Migration results
   */
  runMigration(tenantSlug = null) {
    const params = {};
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-config', {}, { params });
  },

  /**
   * Run current level migration dry run (preview only).
   * @param {string} tenantSlug - Optional tenant slug
   * @returns {Promise} Preview data (totalUsers, migrated, skipped, errors)
   */
  runCurrentLevelDryRun(tenantSlug = null) {
    const params = { dryRun: 'true' };
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-current-level', {}, { params });
  },

  /**
   * Run current level migration (persists to DB).
   * @param {string} tenantSlug - Optional tenant slug
   * @returns {Promise} Migration results
   */
  runCurrentLevelMigration(tenantSlug = null) {
    const params = {};
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-current-level', {}, { params });
  },

  /**
   * Run dev tip dimension rotation migration dry run (preview only).
   * @param {string} tenantSlug - Optional tenant slug
   * @returns {Promise} Preview data (totalUsersMissing, usersMigrated, rotationEntriesAdded, errors)
   */
  runDevTipDimensionRotationDryRun(tenantSlug = null) {
    const params = { dryRun: 'true' };
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-dev-tip-dimension-rotation', {}, { params });
  },

  /**
   * Run dev tip dimension rotation migration (persists to DB).
   * @param {string} tenantSlug - Optional tenant slug
   * @returns {Promise} Migration results
   */
  runDevTipDimensionRotationMigration(tenantSlug = null) {
    const params = {};
    if (tenantSlug) {
      params.tenantSlug = tenantSlug;
    }
    return adminApi.post('/schemas/migrate-dev-tip-dimension-rotation', {}, { params });
  },

  /**
   * Tenant roles: dry-run from legacy config (admin_emails, company_superuser lists → User.roles).
   */
  runTenantRolesDryRun() {
    return adminApi.post('/tenant-roles/migrate-from-config', { dryRun: true });
  },

  /**
   * Tenant roles: apply migration from legacy config.
   */
  runTenantRolesMigration() {
    return adminApi.post('/tenant-roles/migrate-from-config', { dryRun: false });
  },

  /**
   * TEST ONLY: Preview dev tip schedule (parallel/alternate) - next up to 40 occurrences.
   * Optional userId: when provided, dimensions are shown from that user's devTipDimensionRotation.
   */
  getDevTipSchedulePreview(userId) {
    const params = userId ? { userId } : {};
    return adminApi.get('/scheduler/dev-tip-schedule-preview', { params });
  },

  /** Tenant `features` cleanup (normalize rows, strip legacy keys, dedupe legacy type — preview). */
  runFeaturesMigrateDryRun() {
    return adminApi.post('/features/migrate', {}, { params: { dryRun: 'true' } });
  },

  /** Apply tenant `features` cleanup migration for the current tenant. */
  runFeaturesMigrate() {
    return adminApi.post('/features/migrate', {}, { params: { dryRun: 'false' } });
  },

  /**
   * [HOTFIX] 360mindset dimension correction dry run (maximator / app_maximator only).
   */
  run360mindsetDimensionCorrectionDryRun() {
    return adminApi.post('/schemas/migrate-360mindset-dimension-correction', {}, { params: { dryRun: 'true' } });
  },

  /**
   * [HOTFIX] Apply 360mindset dimension correction (maximator / app_maximator only).
   */
  run360mindsetDimensionCorrection() {
    return adminApi.post('/schemas/migrate-360mindset-dimension-correction', {}, { params: {} });
  },

  /** Empty dimension repair dry run (active CMS tenant). */
  runEmptyDimensionRepairDryRun() {
    return adminApi.post('/schemas/migrate-empty-dimension-repair', {}, { params: { dryRun: 'true' } });
  },

  /** Apply empty dimension repair for one assessment (active CMS tenant). */
  runEmptyDimensionRepairOne(assessmentId) {
    return adminApi.post('/schemas/migrate-empty-dimension-repair', {}, { params: { assessmentId } });
  },

  /** Apply empty dimension repair for all flagged assessments (active CMS tenant). */
  runEmptyDimensionRepair() {
    return adminApi.post('/schemas/migrate-empty-dimension-repair', {}, { params: {} });
  },
};
